import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ArrowRightLeft,
  CalendarClock,
  CheckCircle2,
  Landmark,
  Loader2,
  Lock,
  Plus,
  Search,
  Star,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { publicApi } from "@/services/api-client";
import { useIdempotencyKey } from "@/hooks/use-idempotency-key";
import { countryOf, fmt, type Wallet } from "@/types/dashboard";
import {
  EnvironmentBadge,
  Flag,
  KybLock,
  PageHead,
  Panel,
} from "@/components/bits";
import {
  ConfirmDialog,
  ErrorBox,
  KV,
  Seg,
  Tag,
  fmtAmount,
  normalizeAmount,
  pick,
  record,
  rows,
  text,
  useFinanceGate,
  useServer,
  type ApiRecord,
} from "./kit";
import { OperationLookup } from "./operation-lookup";

type TabKey = "conversion" | "comptes-externes" | "reservations" | "suivi";

const FALLBACK_CONVERSION_RULES: Array<{
  source: string;
  target: string;
  rate: number;
  feePct: number;
}> = [
  { source: "GNF", target: "XAF", rate: 1 / 15, feePct: 12 },
  { source: "GNF", target: "XOF", rate: 1 / 15, feePct: 12 },
  { source: "GNF", target: "CDF", rate: 0.26, feePct: 12 },
  { source: "XOF", target: "XAF", rate: 1, feePct: 5 },
  { source: "XOF", target: "GNF", rate: 15, feePct: 7 },
  { source: "XOF", target: "CDF", rate: 3.95, feePct: 11.5 },
  { source: "CDF", target: "XAF", rate: 1 / 3.95, feePct: 11.5 },
  { source: "CDF", target: "XOF", rate: 1 / 3.95, feePct: 11.5 },
  { source: "CDF", target: "GNF", rate: 1 / 0.26, feePct: 6.2 },
  { source: "XAF", target: "XOF", rate: 1, feePct: 6 },
  { source: "XAF", target: "GNF", rate: 15, feePct: 6 },
  { source: "XAF", target: "CDF", rate: 3.95, feePct: 11.5 },
];

export default function FinancePage() {
  const gate = useFinanceGate();
  const [tab, setTab] = useState<TabKey>("conversion");
  const active = useMemo(
    () => gate.d.wallets.filter((w) => w.status === "ACTIVE"),
    [gate.d.wallets],
  );

  if (gate.locked || (!gate.loading && !gate.allowed))
    return (
      <>
        <PageHead title="Trésorerie" />
        <KybLock what="Conversions et réservations" />
      </>
    );

  return (
    <>
      <PageHead
        title="Trésorerie"
        sub="Conversions multidevises (XAF, XOF, GNF, CDF), comptes externes & règlements automatiques, réservations et suivi."
      >
        <EnvironmentBadge />
      </PageHead>
      <div className="mb-5">
        <Seg<TabKey>
          label="Sections de trésorerie"
          value={tab}
          onChange={setTab}
          items={[
            ["conversion", "Conversion"],
            ["comptes-externes", "Comptes externes & Règlements"],
            ["reservations", "Réservations"],
            ["suivi", "Suivi d'opération"],
          ]}
        />
      </div>
      {tab === "conversion" && (
        <Conversion wallets={active} onDone={gate.d.refresh} />
      )}
      {tab === "comptes-externes" && <ExternalAccountsSection />}
      {tab === "reservations" && <Reservations wallets={active} />}
      {tab === "suivi" && (
        <Panel
          title="Suivi manuel"
          sub="Consultez l'état actuel d'un dépôt, retrait ou remboursement. Une requête par clic, sans actualisation cachée."
        >
          <OperationLookup />
        </Panel>
      )}
    </>
  );
}

function WalletSelect({
  id,
  value,
  onChange,
  wallets,
  label,
  exclude,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  wallets: Wallet[];
  label: string;
  exclude?: string;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger id={id} data-testid={`select-${id}`}>
          <SelectValue placeholder="Choisir un wallet actif" />
        </SelectTrigger>
        <SelectContent>
          {wallets
            .filter((w) => w.id !== exclude)
            .map((w) => (
              <SelectItem key={w.id} value={w.id}>
                <span className="flex items-center gap-2">
                  <Flag code={w.country} />
                  {countryOf(w.country).name} · {w.type} ·{" "}
                  {fmt(w.balance, w.currency)}
                </span>
              </SelectItem>
            ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Conversion({
  wallets,
  onDone,
}: {
  wallets: Wallet[];
  onDone: () => Promise<void>;
}) {
  const idem = useIdempotencyKey();
  const rulesRes = useServer<ApiRecord>("/conversions/rules");
  const [src, setSrc] = useState("");
  const [dst, setDst] = useState("");
  const [amount, setAmount] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<ApiRecord | null>(null);
  const s = wallets.find((w) => w.id === src);
  const t = wallets.find((w) => w.id === dst);
  const value = normalizeAmount(amount);
  const tooMuch = s && value ? Number(value) > s.balance : false;

  const rulesList = useMemo(() => {
    const serverRules = rows(record(rulesRes.data)?.rules).map(record);
    if (serverRules.length > 0) {
      return serverRules.map((r) => ({
        source: text(r, "source_currency_code"),
        target: text(r, "target_currency_code"),
        rate: Number(pick(r, "exchange_rate") ?? 0),
        feePct: Number(pick(r, "fee_percentage_value") ?? 0),
        feeFixed: Number(pick(r, "fee_fixed_value") ?? 0),
      }));
    }
    return FALLBACK_CONVERSION_RULES.map((r) => ({ ...r, feeFixed: 0 }));
  }, [rulesRes.data]);

  const preview = useMemo(() => {
    if (!s || !t || !value || s.currency === t.currency) return null;
    const initialAmount = Number(value);
    if (!Number.isFinite(initialAmount) || initialAmount <= 0) return null;

    const matched = rulesList.find(
      (r) =>
        r.source.toUpperCase() === s.currency.toUpperCase() &&
        r.target.toUpperCase() === t.currency.toUpperCase(),
    );
    if (!matched || matched.rate <= 0) return null;

    const srcDecimals = s.currency === "CDF" || s.currency === "USD" ? 2 : 0;
    const tgtDecimals = t.currency === "CDF" || t.currency === "USD" ? 2 : 0;

    // Step 1: Frais = Montant initial * Pourcentage / 100
    const rawFee =
      (initialAmount * matched.feePct) / 100 + (matched.feeFixed || 0);
    const feeAmount = Number(rawFee.toFixed(srcDecimals));
    // Step 2: Montant net = Montant initial - Frais
    const netAmount = Number((initialAmount - feeAmount).toFixed(srcDecimals));
    // Step 3: Montant reçu = Montant net * Taux de change
    const targetAmount = Number(
      (netAmount * matched.rate).toFixed(tgtDecimals),
    );

    const rateDisplay =
      matched.rate >= 1
        ? `1 ${s.currency} = ${Number(matched.rate.toFixed(4))} ${t.currency}`
        : `1 ${t.currency} = ${Number((1 / matched.rate).toFixed(4))} ${s.currency} (1 ${s.currency} = ${Number(matched.rate.toFixed(6))} ${t.currency})`;

    return {
      initialAmount,
      feePct: matched.feePct,
      feeAmount,
      netAmount,
      rate: matched.rate,
      rateDisplay,
      targetAmount,
      sourceCurrency: s.currency,
      targetCurrency: t.currency,
    };
  }, [s, t, value, rulesList]);

  async function run() {
    if (!value) return;
    const base = {
      source_wallet_id: src,
      destination_wallet_id: dst,
      target_wallet_id: dst,
      source_amount: value,
    };
    const idemKey = idem.forPayload(base);
    const body = { ...base, idempotency_key: idemKey };
    setBusy(true);
    setError(null);
    try {
      const { data } = await publicApi<ApiRecord>("/conversions", {
        method: "POST",
        headers: {
          "Idempotency-Key": idemKey,
        },
        body,
      });
      setResult(record(data));
      setConfirm(false);
      idem.reset();
      void onDone();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-5 lg:grid-cols-[1fr_400px]">
      <Panel
        title="Convertir entre deux wallets"
        sub="Les frais sont déduits du montant initial saisi avant d'appliquer le taux de change."
      >
        {wallets.length < 2 ? (
          <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
            Il faut au moins deux wallets actifs pour convertir.
          </p>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setError(null);
              setConfirm(true);
            }}
          >
            <div className="grid items-end gap-3 sm:grid-cols-[1fr_auto_1fr]">
              <WalletSelect
                id="conv-source"
                label="Wallet source"
                value={src}
                onChange={setSrc}
                wallets={wallets}
                exclude={dst}
              />
              <ArrowRightLeft
                className="mx-auto mb-2 hidden h-5 w-5 text-muted-foreground sm:block"
                aria-hidden
              />
              <WalletSelect
                id="conv-dest"
                label="Wallet destination"
                value={dst}
                onChange={setDst}
                wallets={wallets}
                exclude={src}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="conv-amount">
                Montant à convertir {s ? `(${s.currency})` : ""}
              </Label>
              <Input
                id="conv-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Ex: 10000"
                data-testid="input-conv-amount"
              />
              {amount && !value && (
                <p className="text-xs text-destructive">
                  Montant décimal positif requis.
                </p>
              )}
              {tooMuch && (
                <p className="text-xs text-destructive">
                  Supérieur au solde disponible.
                </p>
              )}
              {s && t && s.currency === t.currency && (
                <p className="text-xs text-destructive">
                  Veuillez sélectionner deux wallets de devises différentes.
                </p>
              )}
            </div>

            {/* Live Pre-validation Breakdown (Cahier des charges Section 5) */}
            {preview && (
              <div
                className="rounded-xl border border-primary/20 bg-accent/50 p-4 text-sm space-y-2"
                data-testid="conversion-preview"
              >
                <p className="text-xs font-bold uppercase tracking-wider text-primary">
                  Détail du calcul avant validation
                </p>
                <div className="grid grid-cols-[1fr_auto] gap-y-1.5 text-xs sm:text-sm">
                  <span className="text-muted-foreground">
                    Montant à convertir :
                  </span>
                  <strong className="tnum text-right">
                    {fmt(preview.initialAmount, preview.sourceCurrency)}
                  </strong>

                  <span className="text-muted-foreground">
                    Frais de conversion :
                  </span>
                  <strong className="tnum text-right text-amber-700">
                    {preview.feePct} % (
                    {fmt(preview.feeAmount, preview.sourceCurrency)})
                  </strong>

                  <span className="text-muted-foreground">
                    Montant après frais :
                  </span>
                  <strong className="tnum text-right">
                    {fmt(preview.netAmount, preview.sourceCurrency)}
                  </strong>

                  <span className="text-muted-foreground">Taux appliqué :</span>
                  <strong className="tnum text-right text-xs">
                    {preview.rateDisplay}
                  </strong>

                  <div className="col-span-2 my-1 border-t border-border/60" />

                  <span className="font-semibold text-foreground">
                    Montant à recevoir :
                  </span>
                  <strong className="tnum text-right text-base font-extrabold text-emerald-700">
                    {fmt(preview.targetAmount, preview.targetCurrency)}
                  </strong>
                </div>
              </div>
            )}

            <Button
              type="submit"
              disabled={
                !s ||
                !t ||
                !value ||
                tooMuch ||
                s.currency === t.currency ||
                (preview !== null && preview.netAmount <= 0)
              }
              className="justify-self-start"
              data-testid="button-conv-review"
            >
              Vérifier la conversion
            </Button>
          </form>
        )}
      </Panel>

      <div className="space-y-5">
        <Panel title="Résultat serveur">
          {result ? (
            <div className="space-y-3" data-testid="conversion-result">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Statut</span>
                <Tag value={text(result, "status")} />
              </div>
              <div className="rounded-lg bg-accent/60 p-3 text-sm space-y-1.5">
                <p className="flex justify-between">
                  <span className="text-muted-foreground">
                    Montant initial :
                  </span>
                  <b className="tnum">
                    {fmtAmount(
                      pick(result, "source_amount"),
                      text(result, "source_currency") || s?.currency,
                    )}
                  </b>
                </p>
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Frais déduits :</span>
                  <b className="tnum text-amber-700">
                    {fmtAmount(
                      pick(result, "fx_fee", "fee", "fee_amount", "fees"),
                      text(result, "source_currency") || s?.currency,
                    )}
                  </b>
                </p>
                {pick(result, "net_amount") !== undefined && (
                  <p className="flex justify-between">
                    <span className="text-muted-foreground">
                      Montant net converti :
                    </span>
                    <b className="tnum">
                      {fmtAmount(
                        pick(result, "net_amount"),
                        text(result, "source_currency") || s?.currency,
                      )}
                    </b>
                  </p>
                )}
                <p className="flex justify-between">
                  <span className="text-muted-foreground">Taux appliqué :</span>
                  <b className="tnum">
                    {text(result, "exchange_rate", "applied_rate", "rate") ||
                      "—"}
                  </b>
                </p>
                <div className="border-t pt-1.5 flex justify-between">
                  <span className="font-semibold">Montant crédité :</span>
                  <b className="tnum text-emerald-700">
                    {fmtAmount(
                      pick(result, "target_amount", "destination_amount"),
                      text(result, "target_currency") || t?.currency,
                    )}
                  </b>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              Aucune conversion effectuée dans cette session.
            </p>
          )}
        </Panel>

        <Panel
          title="Grille des taux et frais"
          sub="Frais prélevés sur le montant initial avant conversion."
        >
          <div className="max-h-64 overflow-y-auto text-xs">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="py-1.5 font-semibold">Sens</th>
                  <th className="py-1.5 font-semibold">Taux</th>
                  <th className="py-1.5 text-right font-semibold">Frais</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rulesList.map((r) => (
                  <tr key={`${r.source}-${r.target}`}>
                    <td className="py-1.5 font-mono font-semibold">
                      {r.source} → {r.target}
                    </td>
                    <td className="py-1.5 font-mono text-muted-foreground">
                      {Number(r.rate.toFixed(6))}
                    </td>
                    <td className="py-1.5 text-right font-semibold text-amber-700">
                      {r.feePct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      <ConfirmDialog
        open={confirm}
        title="Confirmer la conversion"
        description="Veuillez vérifier le récapitulatif complet de votre conversion avant validation."
        busy={busy}
        error={error}
        onClose={() => setConfirm(false)}
        onConfirm={() => void run()}
      >
        {preview ? (
          <div className="rounded-lg bg-accent/60 p-3.5 text-sm space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Montant à convertir :
              </span>
              <b className="tnum">
                {fmt(preview.initialAmount, preview.sourceCurrency)}
              </b>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Frais de conversion :
              </span>
              <b className="tnum text-amber-700">
                {preview.feePct} % (
                {fmt(preview.feeAmount, preview.sourceCurrency)})
              </b>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">
                Montant après frais :
              </span>
              <b className="tnum">
                {fmt(preview.netAmount, preview.sourceCurrency)}
              </b>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Taux appliqué :</span>
              <b className="tnum text-xs">{preview.rateDisplay}</b>
            </div>
            <div className="border-t pt-1.5 flex justify-between">
              <span className="font-semibold">Montant à recevoir :</span>
              <b className="tnum text-emerald-700">
                {fmt(preview.targetAmount, preview.targetCurrency)}
              </b>
            </div>
          </div>
        ) : (
          <p className="rounded-lg bg-accent/60 p-3 text-center text-sm">
            <b className="tnum">
              {value} {s?.currency}
            </b>{" "}
            depuis {s && countryOf(s.country).name} vers{" "}
            {t && countryOf(t.country).name} ({t?.currency})
          </p>
        )}
      </ConfirmDialog>
    </div>
  );
}

const EXTERNAL_ACCOUNT_TYPES = [
  { value: "MOBILE_MONEY", label: "Mobile Money" },
  { value: "BANK", label: "Compte Bancaire (BANK)" },
  { value: "WALLET", label: "Portefeuille (WALLET)" },
  { value: "CARD", label: "Carte (CARD)" },
  { value: "CRYPTO", label: "Crypto (CRYPTO)" },
  { value: "OTHER", label: "Autre (OTHER)" },
];

const DEFAULT_PROVIDERS = [
  "MTN Mobile Money",
  "Orange Money",
  "Wave",
  "Moov Money",
  "Airtel Money",
  "Vodacom M-Pesa",
  "Free Money",
  "Virement Bancaire",
];

const SETTLEMENT_FREQUENCIES = [
  { value: "MANUAL", label: "Manuel (MANUAL)" },
  { value: "DAILY", label: "Quotidien (DAILY)" },
  { value: "WEEKLY", label: "Hebdomadaire (WEEKLY)" },
  { value: "MONTHLY", label: "Mensuel (MONTHLY)" },
];

function ExternalAccountsSection() {
  const accountsRes = useServer<unknown>("/external-accounts");
  const settlementRes = useServer<ApiRecord>("/auto-settlement");
  const servicesRes = useServer<ApiRecord>("/dashboard/services");

  const [type, setType] = useState("MOBILE_MONEY");
  const [provider, setProvider] = useState("MTN Mobile Money");
  const [accountNumber, setAccountNumber] = useState("");
  const [label, setLabel] = useState("");
  const [description, setDescription] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<unknown>(null);

  const [frequency, setFrequency] = useState("MANUAL");
  const [settlementAccountId, setSettlementAccountId] = useState("");
  const [isAutomatic, setIsAutomatic] = useState(false);
  const [savingSettlement, setSavingSettlement] = useState(false);
  const [settlementErr, setSettlementErr] = useState<unknown>(null);
  const [settlementSaved, setSettlementSaved] = useState(false);

  const externalAccounts = useMemo(
    () => rows(accountsRes.data).map(record),
    [accountsRes.data],
  );

  const availableProviders = useMemo(() => {
    const set = new Set<string>(DEFAULT_PROVIDERS);
    const catalogServices = rows(servicesRes.data).map(record);
    for (const item of catalogServices) {
      const name = text(
        item,
        "provider_name",
        "label",
        "name",
        "payment_method",
      );
      if (name) set.add(name);
    }
    return Array.from(set);
  }, [servicesRes.data]);

  useEffect(() => {
    const cfg = record(settlementRes.data);
    if (cfg && Object.keys(cfg).length > 0) {
      if (text(cfg, "frequency")) setFrequency(text(cfg, "frequency"));
      if (cfg.account_id) setSettlementAccountId(String(cfg.account_id));
      if (typeof cfg.is_automatic === "boolean")
        setIsAutomatic(cfg.is_automatic);
    } else if (externalAccounts.length > 0 && !settlementAccountId) {
      const def =
        externalAccounts.find((a) => Boolean(a.is_default)) ??
        externalAccounts[0];
      if (def?.id) setSettlementAccountId(String(def.id));
    }
  }, [settlementRes.data, externalAccounts, settlementAccountId]);

  async function handleCreateAccount(e: FormEvent) {
    e.preventDefault();
    if (!accountNumber.trim() || creating) return;
    setCreating(true);
    setCreateErr(null);
    try {
      await publicApi("/external-accounts", {
        method: "POST",
        body: {
          account_number: accountNumber.trim(),
          type,
          provider: provider || null,
          label: label.trim() || null,
          description: description.trim() || null,
          is_default: isDefault,
        },
      });
      setAccountNumber("");
      setLabel("");
      setDescription("");
      setIsDefault(false);
      accountsRes.reload();
    } catch (err) {
      setCreateErr(err);
    } finally {
      setCreating(false);
    }
  }

  async function handleSetDefault(id: number) {
    try {
      await publicApi(`/external-accounts/${id}`, {
        method: "PUT",
        body: { is_default: true },
      });
      accountsRes.reload();
    } catch (err) {
      setCreateErr(err);
    }
  }

  async function handleDelete(id: number) {
    try {
      await publicApi(`/external-accounts/${id}`, {
        method: "DELETE",
      });
      accountsRes.reload();
    } catch (err) {
      setCreateErr(err);
    }
  }

  async function handleSaveSettlement(e: FormEvent) {
    e.preventDefault();
    if (!settlementAccountId || savingSettlement) return;
    setSavingSettlement(true);
    setSettlementErr(null);
    setSettlementSaved(false);
    try {
      await publicApi("/auto-settlement", {
        method: "POST",
        body: {
          frequency,
          account_id: Number(settlementAccountId),
          is_automatic: isAutomatic,
        },
      });
      setSettlementSaved(true);
      settlementRes.reload();
    } catch (err) {
      setSettlementErr(err);
    } finally {
      setSavingSettlement(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="external-accounts-section">
      <div className="rounded-2xl border bg-card p-5">
        <h2 className="text-base font-bold">
          Gérez vos comptes externes et configurez vos règlements automatiques
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Enregistrez vos numéros Mobile Money ou comptes bancaires pour les
          réutiliser lors de vos dépôts, retraits et règlements automatiques.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Create External Account */}
        <Panel
          title="Ajouter un compte externe"
          sub="Ce compte sera disponible dans vos dépôts, retraits et règlements."
        >
          <form onSubmit={handleCreateAccount} className="grid gap-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="ext-type">Type de compte</Label>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger id="ext-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXTERNAL_ACCOUNT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="ext-provider">Opérateur / Service</Label>
                <Select value={provider} onValueChange={setProvider}>
                  <SelectTrigger id="ext-provider">
                    <SelectValue placeholder="Sélectionner un service" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableProviders.map((p) => (
                      <SelectItem key={p} value={p}>
                        {p}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="ext-number">
                  Numéro de compte / téléphone *
                </Label>
                <Input
                  id="ext-number"
                  value={accountNumber}
                  onChange={(e) => setAccountNumber(e.target.value)}
                  placeholder="Ex: 658895572"
                  required
                  data-testid="input-ext-account-number"
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="ext-label">Libellé</Label>
                <Input
                  id="ext-label"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Ex: Compte principal MTN"
                  data-testid="input-ext-label"
                />
              </div>
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="ext-desc">Description (optionnel)</Label>
              <Input
                id="ext-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Notes ou informations complémentaires"
              />
            </div>

            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              Définir comme compte externe par défaut
            </label>

            <ErrorBox error={createErr} />

            <Button
              type="submit"
              disabled={creating || !accountNumber.trim()}
              className="justify-self-start"
              data-testid="button-create-ext-account"
            >
              {creating ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Plus className="mr-2 h-4 w-4" />
              )}
              Enregistrer le compte externe
            </Button>
          </form>
        </Panel>

        {/* Auto-settlement Configuration */}
        <Panel
          title="Règlement automatique (Auto-Settlement)"
          sub="Planifiez le reversement automatique vers l'un de vos comptes externes."
        >
          <form onSubmit={handleSaveSettlement} className="grid gap-4">
            <div className="grid gap-1.5">
              <Label htmlFor="settlement-account">
                Compte externe de destination *
              </Label>
              <Select
                value={settlementAccountId}
                onValueChange={setSettlementAccountId}
                disabled={externalAccounts.length === 0}
              >
                <SelectTrigger id="settlement-account">
                  <SelectValue placeholder="Sélectionner un compte externe" />
                </SelectTrigger>
                <SelectContent>
                  {externalAccounts.map((acc) => {
                    const idStr = String(acc.id);
                    return (
                      <SelectItem key={idStr} value={idStr}>
                        {text(acc, "label") ||
                          text(acc, "provider") ||
                          text(acc, "type")}{" "}
                        — {text(acc, "account_number")}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              {externalAccounts.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Ajoutez d'abord un compte externe pour configurer le règlement
                  automatique.
                </p>
              )}
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="settlement-frequency">Fréquence *</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger id="settlement-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SETTLEMENT_FREQUENCIES.map((f) => (
                    <SelectItem key={f.value} value={f.value}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <label className="flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={isAutomatic}
                onChange={(e) => setIsAutomatic(e.target.checked)}
                className="h-4 w-4 rounded border-input"
              />
              Activer l'exécution automatique selon la fréquence choisie
            </label>

            <ErrorBox error={settlementErr} />

            {settlementSaved && (
              <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 p-2.5 text-xs font-medium text-emerald-800">
                <CheckCircle2 className="h-4 w-4" /> Configuration de règlement
                enregistrée avec succès.
              </p>
            )}

            <Button
              type="submit"
              disabled={savingSettlement || !settlementAccountId}
              className="justify-self-start"
              data-testid="button-save-settlement"
            >
              {savingSettlement ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <CalendarClock className="mr-2 h-4 w-4" />
              )}
              Enregistrer la configuration
            </Button>
          </form>
        </Panel>
      </div>

      {/* Saved External Accounts List */}
      <Panel
        title="Vos comptes externes enregistrés"
        sub={`${externalAccounts.length} compte(s) enregistré(s)`}
      >
        {accountsRes.loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : externalAccounts.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Aucun compte externe enregistré pour le moment.
          </p>
        ) : (
          <div className="divide-y">
            {externalAccounts.map((acc) => {
              const id = Number(acc.id);
              const isDef = Boolean(acc.is_default);
              return (
                <div
                  key={id}
                  className="flex flex-wrap items-center justify-between gap-3 py-3.5"
                >
                  <div className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
                      <Landmark className="h-4 w-4" />
                    </span>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold">
                          {text(acc, "label") ||
                            text(acc, "provider") ||
                            text(acc, "type")}
                        </p>
                        {isDef && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800">
                            <Star className="h-2.5 w-2.5 fill-current" /> Par
                            défaut
                          </span>
                        )}
                        <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                          {text(acc, "type")}
                        </span>
                      </div>
                      <p className="mt-0.5 font-mono text-xs text-foreground">
                        {text(acc, "provider")
                          ? `${text(acc, "provider")} · `
                          : ""}
                        {text(acc, "account_number")}
                      </p>
                      {text(acc, "description") && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {text(acc, "description")}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!isDef && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void handleSetDefault(id)}
                      >
                        Définir par défaut
                      </Button>
                    )}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="text-destructive hover:text-destructive"
                      onClick={() => void handleDelete(id)}
                      aria-label="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Reservations({ wallets }: { wallets: Wallet[] }) {
  const createKey = useIdempotencyKey();
  const actionKey = useIdempotencyKey();
  const [walletId, setWalletId] = useState("");
  const [amount, setAmount] = useState("");
  const [purpose, setPurpose] = useState("");
  const [expires, setExpires] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [lastCreated, setLastCreated] = useState<ApiRecord | null>(null);
  const [lookupId, setLookupId] = useState("");
  const [current, setCurrent] = useState<ApiRecord | null>(null);
  const [lookupErr, setLookupErr] = useState<unknown>(null);
  const [lookupBusy, setLookupBusy] = useState(false);
  const [action, setAction] = useState<"capture" | "release" | null>(null);
  const [actionErr, setActionErr] = useState<unknown>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const wallet = wallets.find((w) => w.id === walletId);
  const value = normalizeAmount(amount);
  const expiresIso = expires ? new Date(expires).toISOString() : "";
  const valid = Boolean(
    wallet &&
    value &&
    purpose.trim().length >= 3 &&
    expires &&
    new Date(expires).getTime() > Date.now(),
  );

  async function create(e: FormEvent) {
    e.preventDefault();
    if (!valid || !value) return;
    const base = {
      wallet_id: walletId,
      operation: "MERCHANT_RESERVATION",
      amount: value,
      currency: wallet?.currency ?? "",
      purpose: purpose.trim(),
      expires_at: expiresIso,
    };
    const operationReference = createKey.forPayload(base);
    setBusy(true);
    setError(null);
    try {
      const { data } = await publicApi<ApiRecord>("/reservations", {
        method: "POST",
        body: { ...base, operation_reference: operationReference },
      });
      const r = record(record(data).reservation ?? data);
      setLastCreated(r);
      setCurrent(r);
      setLookupId(text(r, "id"));
      createKey.reset();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  async function lookup(e?: FormEvent) {
    e?.preventDefault();
    if (!lookupId.trim()) return;
    setLookupBusy(true);
    setLookupErr(null);
    try {
      const { data } = await publicApi<ApiRecord>(
        `/reservations/${encodeURIComponent(lookupId.trim())}`,
      );
      setCurrent(record(record(data).reservation ?? data));
    } catch (err) {
      setCurrent(null);
      setLookupErr(err);
    } finally {
      setLookupBusy(false);
    }
  }

  async function runAction() {
    if (!action || !current) return;
    const id = text(current, "id");
    setActionBusy(true);
    setActionErr(null);
    try {
      const { data } = await publicApi<ApiRecord>(
        `/reservations/${encodeURIComponent(id)}/${action}`,
        {
          method: "POST",
          body: { idempotency_key: actionKey.forPayload({ id, action }) },
        },
      );
      setCurrent(record(record(data).reservation ?? data));
      actionKey.reset();
      setAction(null);
    } catch (err) {
      setActionErr(err);
    } finally {
      setActionBusy(false);
    }
  }

  const status = current ? text(current, "status").toUpperCase() : "";
  const open =
    status &&
    !["CAPTURED", "RELEASED", "EXPIRED", "CANCELLED"].includes(status);

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Panel
        title="Réserver des fonds"
        sub="Bloque un montant sur un wallet jusqu'à capture, libération ou expiration."
      >
        <form onSubmit={create} className="grid gap-4">
          <WalletSelect
            id="res-wallet"
            label="Wallet"
            value={walletId}
            onChange={setWalletId}
            wallets={wallets}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-1.5">
              <Label htmlFor="res-amount">
                Montant {wallet ? `(${wallet.currency})` : ""}
              </Label>
              <Input
                id="res-amount"
                inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="res-exp">Expiration</Label>
              <Input
                id="res-exp"
                type="datetime-local"
                value={expires}
                onChange={(e) => setExpires(e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="res-purpose">Objet</Label>
            <Input
              id="res-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Garantie commande fournisseur 2026-41"
            />
          </div>
          <ErrorBox
            error={error}
            onRetry={() => void create({ preventDefault() {} } as FormEvent)}
          />
          <Button
            type="submit"
            disabled={!valid || busy}
            className="justify-self-start"
            data-testid="button-create-reservation"
          >
            {busy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Lock className="mr-2 h-4 w-4" />
            )}{" "}
            Créer la réservation
          </Button>
        </form>
        {lastCreated && (
          <p
            className="mt-4 rounded-lg bg-emerald-50 p-3 text-xs text-emerald-900"
            data-testid="reservation-last-created"
          >
            Dernière réservation créée :{" "}
            <span className="font-mono font-semibold">
              {text(lastCreated, "id")}
            </span>
            . Notez cet identifiant : aucune liste de réservations n'est
            disponible dans l'API.
          </p>
        )}
      </Panel>
      <Panel title="Consulter une réservation" sub="Recherche par identifiant.">
        <form onSubmit={lookup} className="flex gap-2">
          <Input
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value)}
            placeholder="uuid de réservation"
            className="font-mono"
            aria-label="Identifiant de réservation"
            data-testid="input-reservation-id"
          />
          <Button
            type="submit"
            disabled={lookupBusy || !lookupId.trim()}
            aria-label="Rechercher"
          >
            {lookupBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
          </Button>
        </form>
        <ErrorBox
          className="mt-3"
          error={lookupErr}
          onRetry={() => void lookup()}
        />
        {current && (
          <div className="mt-4 space-y-3" data-testid="reservation-detail">
            <div className="flex items-center justify-between">
              <p className="tnum text-xl font-extrabold">
                {fmtAmount(
                  pick(current, "amount"),
                  pick(current, "currency", "currency_code"),
                )}
              </p>
              <Tag value={status || "—"} />
            </div>
            <KV data={current} />
            <div className="flex gap-2">
              <Button
                size="sm"
                disabled={!open}
                onClick={() => {
                  setActionErr(null);
                  setAction("capture");
                }}
                data-testid="button-capture"
              >
                Capturer
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!open}
                onClick={() => {
                  setActionErr(null);
                  setAction("release");
                }}
                data-testid="button-release"
              >
                Libérer
              </Button>
            </div>
          </div>
        )}
      </Panel>
      <ConfirmDialog
        open={Boolean(action)}
        title={
          action === "capture"
            ? "Capturer la réservation"
            : "Libérer la réservation"
        }
        description={
          action === "capture"
            ? "Le montant réservé sera définitivement débité."
            : "Le montant réservé redeviendra disponible sur le wallet."
        }
        destructive={action === "capture"}
        busy={actionBusy}
        error={actionErr}
        onClose={() => setAction(null)}
        onConfirm={() => void runAction()}
      />
    </div>
  );
}
